"use client";

import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { Book, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";

export function AboutDisplay() {
  const { profileData } = useProfileDataStore();
  const data = profileData?.about_section;
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const { t: tCommon } = useTranslation("common");
  const { open } = useProfileEditDialogStore();

  if (!data) return null;

  const sanitizedDescription = DOMPurify.sanitize(data.description, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "s",
      "h1",
      "h2",
      "h3",
      "ul",
      "ol",
      "li",
      "span",
      "blockquote",
      "pre",
      "code",
      "a",
    ],
    ADD_TAGS: ["h1", "h2", "h3", "blockquote", "pre"],
    ALLOWED_ATTR: ["href", "target", "rel", "style", "class"],
    KEEP_CONTENT: true,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
    FORCE_BODY: false,
  });

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-blue-400/50 shadow-2xl backdrop-blur-xl">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-15 -left-15 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-cyan-600" />
        <div className="absolute -right-15 -bottom-15 h-32 w-32 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500" />
      </div>

      <CardHeader className="relative flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl p-2">
            <Icon
              className={"text-blue-400"}
              height={"1.25rem"}
              icon={"mdi:about-circle-outline"}
              width={"1.25rem"}
            />
          </div>
          {t("profile:aboutSection.title")}
        </motion.h2>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={
                  "bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 rounded-xl backdrop-blur-sm"
                }
                size="sm"
                variant="ghostSecondary"
                onClick={() => open("about")}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{tCommon("edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>

      <CardContent className="relative space-y-6">
        {!data.description && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="relative h-[120px] w-full space-y-2 rounded-2xl border border-dashed border-blue-300/50 bg-blue-500/5 backdrop-blur-sm"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Button
              className="absolute top-0 left-0 flex h-full w-full items-center rounded-2xl bg-transparent hover:bg-blue-500/10"
              onClick={() => open("about")}
            >
              <Pencil className="mr-3 h-6 w-6 text-blue-400" />
              <h3 className="text-accent-foreground text-lg font-medium">
                {t("profile:aboutSection.description")}
              </h3>
            </Button>
          </motion.div>
        )}
        {data.description && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="rounded-2xl bg-transparent p-4 backdrop-blur-sm hover:bg-blue-500/2"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <style>{`
            .wysiwyg-content {
              color: hsl(var(--secondary-foreground));
              font-size: 0.875rem;
              line-height: 1.5;
              text-wrap: wrap;
              break-words: break-word;
              min-height: 0 !important;
            }
            
            .wysiwyg-content h1 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-bottom: 0.75rem;
            }
            
            .wysiwyg-content h2 {
              font-size: 1.25rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
            }
            
            .wysiwyg-content h3 {
              font-size: 1.125rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
            }
            
            .wysiwyg-content p {
              margin-bottom: 0.5rem;
            }
            
            .wysiwyg-content ul,
            .wysiwyg-content ol {
              padding-left: 1.5rem;
              margin-bottom: 0.5rem;
            }
            
            .wysiwyg-content ul li {
              list-style-type: disc;
            }
            
            .wysiwyg-content ol li {
              list-style-type: decimal;
            }
            
            .wysiwyg-content blockquote {
              border-left: 3px solid hsl(var(--border));
              margin-left: 0;
              margin-right: 0;
              padding-left: 1rem;
              color: hsl(var(--muted-foreground));
            }
            
            .wysiwyg-content pre {
              background-color: hsl(var(--muted));
              border-radius: 0.25rem;
              padding: 0.75rem;
              font-family: monospace;
              overflow-x: auto;
              margin-bottom: 0.5rem;
            }
            
            .wysiwyg-content a {
              color: hsl(var(--primary));
              text-decoration: underline;
            }
          `}</style>
            <div
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              className="wysiwyg-content"
            />
          </motion.div>
        )}

        {data.main_subjects && Object.keys(data.main_subjects).length > 0 && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="space-y-3"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="text-accent-foreground flex items-center gap-3">
              <div className="rounded-xl p-2">
                <Book className="h-5 w-5 text-blue-400" />
              </div>
              <h3 className="text-base font-medium">{t("profile:aboutSection.mainSubjects")}</h3>
            </div>
            <div className="flex flex-wrap gap-2 ps-12">
              {Object.values(data.main_subjects)
                .slice(0, 4)
                .map(({ subject }) => (
                  <div
                    key={subject.id}
                    className="flex items-center gap-2 rounded-xl bg-blue-500/5 px-3 py-2 text-blue-300 backdrop-blur-sm hover:bg-blue-500/10"
                  >
                    <Icon className="flex h-4 w-4 items-center" icon={subject.icon} />
                    <span className="font-medium">{subject.name}</span>
                  </div>
                ))}
              {Object.values(data.main_subjects).length > 4 && (
                <div className="flex items-center gap-2 rounded-xl bg-blue-500/5 px-3 py-2 text-blue-300 backdrop-blur-sm hover:bg-blue-500/10">
                  <span className="font-medium">
                    +{Object.values(data.main_subjects).length - 4}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
